// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */
import uuid from 'uuid';
import {fetchContext} from 'cchome-shared/utils';
import getConfigApps from '../config';
import {THOR_STATUS} from '../constants/appsConstants';
import {svcNameMap, ANALYTICS_PAGE_TYPES} from '../constants/AnalyticsConstants';
import appsUtil from './AppsUtil';
import thorUtil from './wam/ThorUtil';
import DownloadUtil from './DownloadUtil';
import CCDUtil from './CCDUtil';

const constants = {
	source: 'CCHome',
	apiKey: 'cchome-web-service',
	xApiKey: 'cchome-web-service',
	project: 'cchome-web-service',
	clientID: 'CCHomeWeb1',
	splitEvent: 'split',
	sophiaProfileSetEventType: 'set',
	sophiaProfileSetEventSubtype: 'cohort',
	coreRuleFailEventType: 'core-rule-fail-profile-set',
	coreRuleFailSubtype: 'page'
};

/**
 * A class for initializing the Ingest module
 */
class AnalyticsUtil {
	contextGuid: string;
	contextParams: string;
	contextInit: boolean;
	contextInitialized: boolean;
	osDeprecationEventConfig: Object;

	setContext() {
		if (!this.contextInitialized) {
			const domain = getConfigApps('COOKIE_DOMAIN');
			const secure = getConfigApps('COOKIE_SECURE');
			// $FlowFixMe - bug https://github.com/facebook/flow/issues/6580
			[this.contextGuid, this.contextParams, this.contextInit] = fetchContext(uuid, domain, secure);
			this.contextInitialized = true;
		}
	}

	initAnalytics() {
		const initObj: Object = {};
		initObj.xApiKey = constants.xApiKey;
		initObj.apiKey = constants.apiKey;
		initObj.clientId = constants.clientID;
		initObj.project = constants.project;
		initObj.source = constants.source;
		initObj.environment = 'stage'; // config.env;
		initObj.useLocalReferrer = true;
		initObj.referrerStorageName = 'creative-cloud-referrer';
		return initObj;
	}

	getEventConfigOnCTAClickOfOsDeprecation(config: Object) {
		const eventSubType = config.eventSubType;
		const hasThor = config.hasThor;
		const isSourcePDP = config.isSourcePDP;
		const isPostPurchase = config.isPostPurchase;
		const componentVersion = config.componentVersion;
		const eventGuidOutcome = config.outcome;
		const frameWorkName = config.frameworkName;
		if (eventSubType && eventSubType === 'learn-more' && this.osDeprecationEventConfig) {
			this.osDeprecationEventConfig.eventSubType = 'learn-more';
			return this.osDeprecationEventConfig;
		}
		const getOutcome = (actionName, eventGuidOutcome) => {
			if (!eventGuidOutcome) {
				switch (actionName) {
					case 'install-incompatible':
					case 'download-incompatible':
						return 'false';
					default:
						return 'true';
				}
			}
			return eventGuidOutcome;
		};
		const getEventContextGuid = (actionName, eventGuidOutcome, hasThor) => {
			const getEventContextGuid = `os-incompatible/entry=${isPostPurchase ? 'post-purchase' : 'cch'}/thor=${(hasThor || false).toString()}/outcome=${getOutcome(actionName, eventGuidOutcome)}/modal=${isSourcePDP ? 'App Detail' : 'App'}`;
			return getEventContextGuid;
		};
		const getFrameWorkName = (actionName, hasThor) => {
			switch (actionName) {
				case 'install':
				case 'install-older-version':
					return 'Install Modal';
				case 'download':
				case 'download-older-version':
					return 'Kaizen Modal';
				case 'install-incompatible':
				case 'download-incompatible':
					return 'PDP w/dead end';
				case 'learn-more':
					return hasThor ? 'Install Modal' : 'Kaizen Modal';
			}
		};
		const getComponentVersion = (actionName) => {
			switch (actionName) {
				case 'install':
				case 'download':
					return 'auto-previous';
				case 'install-older-version':
				case 'download-older-version':
				case 'learn-more':
					return 'explict-previous';
				case 'install-incompatible':
				case 'download-incompatible':
					return 'dead-end';
			}
		};
		const getUiViewType = (isSourcePDP) => {
			const uiViewType = isSourcePDP ? 'App Detail' : 'App';
			return uiViewType;
		};
		const getEventValue = (isPostPurchase) => {
			const eventValue = isPostPurchase ? 'auto' : '';
			return eventValue;
		};
		return {
			eventSubType,
			uiViewType: getUiViewType(isSourcePDP),
			eventValue: getEventValue(isPostPurchase),
			eventContextGUID: getEventContextGuid(eventSubType, eventGuidOutcome, hasThor),
			frameworkName: frameWorkName || getFrameWorkName(eventSubType, hasThor),
			componentVersion: componentVersion || getComponentVersion(eventSubType)
		};
	}

	isSplitEvent(dataObject: Object) {
		return dataObject.setEventType && dataObject.setEventType.toLowerCase() === constants.splitEvent;
	}

	isSophiaProfileSetEvent(dataObject: Object) {
		return dataObject.setEventType && dataObject.setEventType.toLowerCase() === constants.sophiaProfileSetEventType
			&& dataObject.setEventSubtype && dataObject.setEventSubtype.toLowerCase() === constants.sophiaProfileSetEventSubtype;
	}

	isCoreRuleFailEvent(dataObject: Object) {
		return dataObject.setEventType && dataObject.setEventType.toLowerCase() === constants.coreRuleFailEventType
			&& dataObject.setEventSubtype && dataObject.setEventSubtype.toLowerCase() === constants.coreRuleFailSubtype;
	}
}

export default new AnalyticsUtil();

function getAnalyticsPageType() {
	if (appsUtil.isCCHInLMS()) {
		return window.isCCHInLMSCanvas ? ANALYTICS_PAGE_TYPES.LMS_CANVAS : ANALYTICS_PAGE_TYPES.LMS_BLACKBOARD;
	}

	const isLoggedOut = appsUtil.isLoggedOutExp();
	if (isLoggedOut) {
		if (appsUtil.isLoggedOutCampaignExp) {
			return ANALYTICS_PAGE_TYPES.EMAIL;
		}
		let source = ANALYTICS_PAGE_TYPES.DOWNLOAD;
		if (CCDUtil.isSEOCCDownloadPage() && !DownloadUtil.checkInputUrlValidity(document.referrer, ['adobe.com'])) {
			if (appsUtil.isSearchEngineResult()) {
				source = ANALYTICS_PAGE_TYPES.SEO;
			}
			else {
				source = ANALYTICS_PAGE_TYPES.ORGANIC;
			}
		}
		return source;
	}

	if (appsUtil.isPostPurchaseUser()) {
		return ANALYTICS_PAGE_TYPES.CHECKOUT;
	};
	return ANALYTICS_PAGE_TYPES.CATALOG;
}

// Get value of env.com.name analytics field based on page type
export function getComName() {
	const comNameComponents = [getAnalyticsPageType(), ''];
	comNameComponents[1] = comNameComponents[0] === ANALYTICS_PAGE_TYPES.CATALOG ? 'Catalog' : 'CCH';
	return comNameComponents.join(':');
}

// Get the name of app download/install worflow, determined from checking thor/wam connection
export function getInstallWorkflowName() {
	let workflow = THOR_STATUS.KAIZEN;
	if (appsUtil.hasThor !== null && appsUtil.hasThor) {
		workflow = THOR_STATUS.THOR;
	}
	if (thorUtil.getWAMClient().isConnected()) {
		workflow = THOR_STATUS.WAM;
	}
	return workflow;
}

// Get value for env.svc.name analytics field based on download workflow name
export function getSvcName() {
	return svcNameMap[getInstallWorkflowName()];
}
